import { Box, CircularProgress, Tooltip, Typography, alpha } from "@mui/material"
import { DataGrid, GridColDef } from "@mui/x-data-grid"
import { GridInitialStateCommunity } from "@mui/x-data-grid/models/gridStateCommunity"
import { getAdvertiserNameById } from "advertiser/tools/common"
import { translate } from "app/language/service"
import { getFormatedNumber } from "app/tools/number"
import { dateSVC } from "app/util/date/service"
import {
  CAMPAIGN_STATUSES,
  FILTER_EXPANDED_HEIGHT_IN_REM,
  FILTER_HEADER_HEIGHT_IN_REM,
  ICampaign,
  ICampaignList
} from "campaign/model"
import { refreshCampaignList } from "campaign/tools/list"
import { sortStatus } from "campaign/tools/status"
import { getCurrencySymbol, getEcpm } from "form/inventory/tools/common"
import { messageSVC } from "message/service"
import { getOrderNameById } from "order/tools/common"
import { CAMPAIGN_SUMMARY_PATH, DSP_DEAL_SUMMARY_PATH, FORECAST_SUMMARY_PATH } from "page/model"
import { navigate } from "page/tools/navigate"
import React from "react"
import { ColumnHeader } from "ui/components/ColumnHeader"
import { FlexBox } from "ui/components/FlexBox"
import { TestHiddenData } from "ui/components/TestHiddenData"
import { getDataGridTexts } from "ui/tools/list"
import { ListIdCell } from "../../../ui/components/ListIdCell"
import { CampaignStatus } from "../misc/CampaignStatus"
import { DeliveryIndex } from "./DeliveryIndex"
import { ForecastTags } from "./ForecastTags"
import { ListActions } from "./ListActions"
import { ReportsCell } from "./ReportsCell"

interface IListProps {
  list: ICampaignList
}

const Loader = () => <CircularProgress sx={{ color: theme => theme.palette.primary.main, width: "1rem" }} size="20" />

export const List = React.memo(
  ({
    list: {
      isFilterExpanded,
      filteredEntities,
      isRefreshing,
      hasRefreshError,
      pagination,
      isLoadingReports,
      isLoadingEstimates,
      rowCount,
      isDspDeal,
      isForecast
    }
  }: IListProps): JSX.Element => {
    // first render use effect
    React.useEffect(() => {
      refreshCampaignList({ isForecast, isDspDeal })
    }, [])
    const columns: Array<GridColDef<ICampaign>> = [
      {
        field: "product",
        headerName: translate("label.name"),
        minWidth: 180,
        flex: 2,
        renderCell: params => (
          <ListIdCell
            label={params.row.product || ""}
            id={params.row.id}
            onClick={() => {
              if (isDspDeal && !params.row.dspDeal) {
                messageSVC.error(translate("dsp-deal.error.editing"))
                return
              }
              navigate({
                path: isForecast ? FORECAST_SUMMARY_PATH : isDspDeal ? DSP_DEAL_SUMMARY_PATH : CAMPAIGN_SUMMARY_PATH,
                pathParams: params.row.id
              })
            }}
          />
        )
      }
    ]
    if (isForecast) {
      columns.push({
        field: "description",
        sortable: false,
        headerName: translate("label.description"),
        minWidth: 270,
        flex: 2,
        renderCell: params => (
          <Tooltip title={params.row.description || ""}>
            <Typography
              color="textPrimary"
              fontSize=".8rem"
              fontWeight="300"
              sx={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis"
              }}>
              {params.row.description || ""}
            </Typography>
          </Tooltip>
        )
      })
    }
    if (isDspDeal) {
      columns.push({
        field: "dspDeal.description",
        sortable: false,
        headerName: translate("label.description"),
        minWidth: 270,
        flex: 2,
        renderCell: params => (
          <Tooltip title={params.row.dspDeal?.description || ""}>
            <Typography
              color="textPrimary"
              fontSize=".8rem"
              fontWeight="300"
              sx={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis"
              }}>
              {params.row.dspDeal?.description || ""}
            </Typography>
          </Tooltip>
        )
      })
    }
    if (!isForecast) {
      columns.push({
        field: "order_name", // server_side field used only for sort
        headerName: translate("label.order"),
        minWidth: 180,
        flex: 1,
        renderCell: params => (
          <ListIdCell label={getOrderNameById(params.row.order_id || -1)} id={params.row.order_id || -1} />
        )
      })
      if (!isDspDeal) {
        columns.push({
          field: "advertiser_name", // server_side field used only for sort
          headerName: translate("label.advertiser"),
          minWidth: 110,
          flex: 1,
          renderCell: params => (
            <ListIdCell
              label={getAdvertiserNameById(params.row.advertiser_id || -1)}
              id={params.row.advertiser_id || -1}
            />
          )
        })
      }
      columns.push({
        field: "status_id",
        headerName: translate("label.status"),
        align: "left",
        minWidth: 110,
        flex: 1,
        sortComparator: (_1, _2, cell1, cell2) =>
          sortStatus(cell1.value as CAMPAIGN_STATUSES, cell2.value as CAMPAIGN_STATUSES),
        renderCell: params => (
          <CampaignStatus
            id={`status-cell-${params.row.id}`}
            status={params.row.status_id}
            forList
            isDspDeal={isDspDeal}
          />
        )
      })
      if (!isDspDeal) {
        columns.push({
          field: "delivery-index", // field doesn't exist
          headerName: translate("label.delivery.index"),
          sortable: false,
          minWidth: 120,
          flex: 1,
          renderCell: params => <DeliveryIndex campaign={params.row} />
        })
      }
    } else {
      columns.push({
        field: "tags",
        sortable: false,
        headerName: translate("label.tag.title"),
        minWidth: 180,
        flex: 2,
        renderCell: params => <ForecastTags campaignId={params.row.id} />
      })
    }
    if (!isDspDeal) {
      columns.push({
        field: "total_spend",
        renderHeader: () => (
          <ColumnHeader
            primaryText={translate("label.budget")}
            secondaryText={translate(isForecast ? "label.planned" : "label.spend.over.planned")}
          />
        ),
        minWidth: 180,
        sortable: false,
        flex: 2,
        renderCell: params =>
          isForecast ? (
            params.row.budget ? (
              `${params.row.budget} ${getCurrencySymbol(params.row.currency_id)}`
            ) : (
              "-"
            )
          ) : (
            <ReportsCell
              isLoadingReports={isLoadingReports}
              reportsValue={params.row.reports ? params.row.reports.spend : null}
              wantedValue={params.row.budget}
              currencySymbol={getCurrencySymbol(params.row.currency_id)}
            />
          )
      })
      columns.push({
        field: "ecpm",
        renderHeader: () => (
          <ColumnHeader
            primaryText={translate("label.ecpm.average")}
            secondaryText={translate(isForecast ? "label.estimated" : "label.spend.over.estimated")}
          />
        ),
        minWidth: 180,
        sortable: false,
        flex: 2,
        renderCell: params =>
          isForecast ? (
            isLoadingEstimates ? (
              <Loader />
            ) : params.row.estimate ? (
              `${getEcpm(params.row.estimate.total_spend, params.row.estimate.total_impressions)} ${getCurrencySymbol(
                params.row.currency_id
              )}`
            ) : (
              "-"
            )
          ) : (
            <ReportsCell
              isLoadingReports={isLoadingReports}
              isLoadingWanted={isLoadingEstimates}
              reportsValue={params.row.reports ? params.row.reports.spend : null}
              wantedValue={params.row.estimate ? params.row.estimate.total_spend : null}
              currencySymbol={getCurrencySymbol(params.row.currency_id)}
            />
          )
      })
      columns.push({
        field: "screens",
        renderHeader: () => (
          <ColumnHeader
            primaryText={translate("label.screen.ratio.primary")}
            secondaryText={translate(isForecast ? "label.estimated" : "label.delivered.over.estimated")}
          />
        ),
        minWidth: 180,
        sortable: false,
        flex: 2,
        renderCell: params =>
          isForecast ? (
            isLoadingEstimates ? (
              <Loader />
            ) : params.row.estimate ? (
              getFormatedNumber(params.row.estimate.total_screens)
            ) : (
              "-"
            )
          ) : (
            <ReportsCell
              isLoadingReports={isLoadingReports}
              isLoadingWanted={isLoadingEstimates}
              reportsValue={
                params.row.reports && params.row.reports.screen_count ? params.row.reports.screen_count : null
              }
              wantedValue={params.row.estimate ? params.row.estimate.total_screens : null}
            />
          )
      })
      columns.push({
        field: "impressions-with",
        renderHeader: () => (
          <ColumnHeader
            primaryText={translate("label.impressions")}
            secondaryText={translate("label.impression.with.multiplier")}
            thirdText={translate(isForecast ? "label.estimated" : "label.delivered.over.estimated")}
          />
        ),
        minWidth: 180,
        sortable: false,
        flex: 2,
        renderCell: params =>
          isForecast ? (
            isLoadingEstimates ? (
              <Loader />
            ) : params.row.estimate ? (
              getFormatedNumber(params.row.estimate.total_impressions)
            ) : (
              "-"
            )
          ) : (
            <ReportsCell
              isLoadingReports={isLoadingReports}
              isLoadingWanted={isLoadingEstimates}
              reportsValue={params.row.reports ? params.row.reports.impressions : null}
              wantedValue={params.row.estimate ? params.row.estimate.total_impressions : null}
            />
          )
      })
    }
    if (!isForecast && !isDspDeal) {
      columns.push({
        field: "impressions-without",
        renderHeader: () => (
          <ColumnHeader
            primaryText={translate("label.impressions")}
            secondaryText={translate("label.impression.no.multiplier")}
            thirdText={translate("label.delivered")}
          />
        ),
        minWidth: 180,
        sortable: false,
        flex: 2,
        renderCell: params =>
          isLoadingReports ? <Loader /> : params.row.reports ? getFormatedNumber(params.row.reports.buys) : ""
      })
    }
    columns.push({
      field: "start_date",
      headerName: translate("label.startDate"),
      minWidth: 90,
      flex: 1,
      renderCell: params => (params.row.start_date ? dateSVC.format(params.row.start_date) : "")
    })
    columns.push({
      field: "end_date",
      headerName: translate("label.endDate"),
      minWidth: 90,
      flex: 1,
      renderCell: params => (params.row.end_date ? dateSVC.format(params.row.end_date) : "")
    })
    columns.push({
      field: "created_at",
      headerName: translate("label.creationDate"),
      minWidth: 100,
      flex: 1,
      renderCell: params => (params.row.created_at ? dateSVC.format(params.row.created_at) : "")
    })
    columns.push({
      field: "language_id",
      headerName: translate("label.language"),
      minWidth: 90,
      flex: 1,
      renderCell: params => (params.row.language_id || "").toUpperCase()
    })
    columns.push({
      field: "actions",
      headerName: translate("label.actions"),
      minWidth: isForecast ? 135 : 170,
      sortable: false,
      flex: 2,
      renderCell: params => <ListActions campaign={params.row} isForecast={isForecast} isDspDeal={isDspDeal} />
    })
    const initialState: GridInitialStateCommunity = {
      pagination: {
        page: pagination.page,
        pageSize: pagination.pageSize
      }
    }
    if (pagination.orderBy && pagination.order) {
      initialState.sorting = {
        sortModel: [{ field: pagination.orderBy, sort: pagination.order }]
      }
    }
    return (
      <Box
        id={isDspDeal ? "dsp-deal-list" : isForecast ? "forecast-list" : "campaign-list"}
        sx={{
          "position": "relative",
          "width": "100%",
          "minWidth": "100%",
          "maxWidth": "100%",
          "display": "flex",
          "flexDirection": "column",
          "alignItems": "center",
          "justifyContent": "center",
          "pl": ".4rem",
          "pr": ".4rem",
          "backgroundColor": theme => theme.palette.background.paper,
          "height": `calc(100% - ${
            isFilterExpanded ? 9 + FILTER_EXPANDED_HEIGHT_IN_REM : 9 + FILTER_HEADER_HEIGHT_IN_REM
          }rem)`,
          "minHeight": `calc(100% - ${
            isFilterExpanded ? 9 + FILTER_EXPANDED_HEIGHT_IN_REM : 9 + FILTER_HEADER_HEIGHT_IN_REM
          }rem)`,
          "maxHeight": `calc(100% - ${
            isFilterExpanded ? 9 + FILTER_EXPANDED_HEIGHT_IN_REM : 9 + FILTER_HEADER_HEIGHT_IN_REM
          }rem)`,
          "transition": "all 400ms ease",
          "& > *": {
            width: "100%"
          },
          "& .MuiDataGrid-root": {
            border: "none"
          },
          "& .MuiDataGrid-virtualScrollerRenderZone > div:nth-of-type(odd)": {
            backgroundColor: theme => theme.palette.background.default
          },
          "& .MuiDataGrid-virtualScrollerRenderZone > div:nth-of-type(odd):hover": {
            backgroundColor: "rgba(0, 0, 0, 0.04)"
          },
          "& .MuiDataGrid-cell": {
            fontSize: ".8rem",
            fontWeight: 300
          },
          "& .MuiDataGrid-overlay": {
            display: hasRefreshError ? "none" : undefined
          }
        }}>
        <TestHiddenData id={`campaign-list-${isRefreshing ? "loading" : "loaded"}`} />
        <DataGrid
          rows={filteredEntities}
          columns={columns}
          loading={isRefreshing}
          className="campaign-list"
          onPageSizeChange={pageSize =>
            refreshCampaignList({ isForecast, isDspDeal, pagination: { ...pagination, pageSize } })
          }
          onPageChange={page => refreshCampaignList({ isForecast, isDspDeal, pagination: { ...pagination, page } })}
          page={pagination.page}
          rowsPerPageOptions={[10, 25, 50]}
          paginationMode="server"
          sortingMode="server"
          onSortModelChange={model => {
            const item = model[0]
            if (!item || !item.sort) {
              refreshCampaignList({
                isForecast,
                isDspDeal,
                pagination: {
                  ...pagination,
                  order: undefined,
                  orderBy: undefined
                }
              })
            } else {
              refreshCampaignList({
                isForecast,
                isDspDeal,
                pagination: {
                  ...pagination,
                  orderBy: item.field,
                  order: item.sort as any
                }
              })
            }
          }}
          rowCount={rowCount}
          initialState={initialState}
          localeText={getDataGridTexts(hasRefreshError ? "-" : undefined)}
        />
        {hasRefreshError && (
          <FlexBox
            fullWidth
            sx={{
              position: "absolute",
              height: "calc(100% - 3.5rem)",
              top: "3.5rem",
              backgroundColor: theme => alpha(theme.palette.background.paper, 0.4)
            }}>
            <Typography color="error" fontSize="1rem" fontWeight={600}>
              {translate("label.list.error")}
            </Typography>
          </FlexBox>
        )}
      </Box>
    )
  }
)
